"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Carousel, useCarousel } from "./carousel";

interface DotsCarouselSlideProps {
  index: number;
  render: (isActive: boolean) => ReactNode;
  slideCount: number;
  testId: string;
}

function DotsCarouselSlide(props: Readonly<DotsCarouselSlideProps>) {
  const { selectedIndex } = useCarousel();
  const isActive = selectedIndex === props.index;

  return (
    // biome-ignore lint/a11y/useSemanticElements: ARIA's carousel pattern uses group for each slide.
    <div
      aria-hidden={!isActive}
      aria-label={`${props.index + 1} / ${props.slideCount}`}
      aria-roledescription="slide"
      className="min-w-0 shrink-0 grow-0 basis-full"
      data-testid={props.testId}
      inert={isActive ? undefined : true}
      role="group"
    >
      {props.render(isActive)}
    </div>
  );
}

export interface DotsCarouselProps<TSlide extends { id: string }> {
  slides: readonly TSlide[];
  /**
   * Renders one slide. `isActive` lets a slide mount expensive media (video)
   * only while it is the selected one.
   */
  renderSlide: (
    slide: TSlide,
    state: { index: number; isActive: boolean }
  ) => ReactNode;
  ariaLabel: string;
  /** Places the inset dot navigation; the only styling that differs per host. */
  dotNavigationClassName: string;
  slideTestId: string;
  className?: string;
  /** Carousel identity (content_id) read by the homepage tracker. */
  trackingName?: string;
  autoplay?: boolean;
  autoplayDelay?: number;
  goToSlideText?: string;
  slideNavigationText?: string;
  pauseAutoplayText?: string;
  resumeAutoplayText?: string;
}

/**
 * Looping, dots-only carousel of full-bleed banners, with the slide semantics
 * the ARIA carousel pattern asks for. Shared by the fullscreen hero slider and
 * the contained D2C slider, which differ only in where the dots sit.
 */
export function DotsCarousel<TSlide extends { id: string }>(
  props: Readonly<DotsCarouselProps<TSlide>>
) {
  return (
    <section
      aria-label={props.ariaLabel}
      aria-roledescription="carousel"
      className={cn("relative overflow-hidden", props.className)}
      data-carousel-name={props.trackingName}
    >
      <Carousel
        activeDotClassName="bg-white"
        autoplay={props.autoplay}
        autoplayControlClassName="text-white"
        autoplayDelay={props.autoplayDelay}
        behavior="carousel"
        dotButtonClassName="text-white"
        dotClassName="bg-white/40"
        dotNavigationClassName={props.dotNavigationClassName}
        goToSlideText={props.goToSlideText}
        navigationLayout="dots"
        navigationMinItems={2}
        nextSlideText="Next slide"
        pauseAutoplayText={props.pauseAutoplayText}
        previousSlideText="Previous slide"
        resumeAutoplayText={props.resumeAutoplayText}
        showAutoplayControl={props.autoplay}
        showNavigationArrows={false}
        showPagination
        slideNavigationText={props.slideNavigationText}
        trackClassName="ml-0"
        viewportClassName="px-0"
      >
        {props.slides.map((slide, index) => (
          <DotsCarouselSlide
            index={index}
            key={slide.id}
            render={(isActive) => props.renderSlide(slide, { index, isActive })}
            slideCount={props.slides.length}
            testId={props.slideTestId}
          />
        ))}
      </Carousel>
    </section>
  );
}
